import { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import LandingPage from './components/LandingPage';
import Signup from './components/Signup';
import LoginModal from './components/LoginModal';
import DashboardNew from './components/DashboardNew';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    fetchIndustries();
    fetchPlatforms();
    
    // Check for saved user session on mount
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setCurrentUser(user);
        
        // Apply saved theme
        if (user.theme_mode) {
          document.documentElement.classList.remove('dark', 'light');
          document.documentElement.classList.add(user.theme_mode);
        }
      } catch (error) {
        console.error('Error loading saved user:', error);
        localStorage.removeItem('currentUser');
      }
    }
  }, []);
  
  // Apply theme and accent color whenever currentUser changes
  useEffect(() => {
    if (currentUser) {
      // Apply theme mode
      if (currentUser.theme_mode) {
        document.documentElement.classList.remove('dark', 'light');
        document.documentElement.classList.add(currentUser.theme_mode);
      }
      
      // Apply accent color - Only purple uses gradient, others use AI-inspired soft colors
      if (currentUser.theme_accent_color) {
        const accentColors = {
          orange: { from: '#e07a5f', to: '#e07a5f', via: '#e07a5f', isGradient: false },
          blue: { from: '#5b8def', to: '#5b8def', via: '#5b8def', isGradient: false },
          purple: { from: '#6B46C1', to: '#A78BFA', via: '#8B5CF6', isGradient: true },
          green: { from: '#6dbf8b', to: '#6dbf8b', via: '#6dbf8b', isGradient: false },
          red: { from: '#e07a7a', to: '#e07a7a', via: '#e07a7a', isGradient: false },
          gold: { from: '#d4a853', to: '#d4a853', via: '#d4a853', isGradient: false }
        };
        
        const colors = accentColors[currentUser.theme_accent_color] || accentColors.purple;
        document.documentElement.style.setProperty('--accent-from', colors.from);
        document.documentElement.style.setProperty('--accent-to', colors.to);
        document.documentElement.style.setProperty('--accent-via', colors.via);
        
        // Add/remove gradient class based on selected color
        if (colors.isGradient) {
          document.documentElement.classList.add('accent-uses-gradient');
          document.documentElement.classList.remove('accent-uses-solid');
        } else {
          document.documentElement.classList.remove('accent-uses-gradient');
          document.documentElement.classList.add('accent-uses-solid');
        }
      }
    }
  }, [currentUser]);

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(`${API}/industries`);
      setIndustries(response.data.industries);
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/platforms`);
      setPlatforms(response.data.platforms);
    } catch (error) {
      console.error('Error fetching platforms:', error);
    }
  };

  const handleSignupSuccess = (user) => {
    setCurrentUser(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
    setShowSignup(false);
  };
  
  const handleLoginSuccess = (user) => {
    setCurrentUser(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
    setShowLogin(false);
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
  };

  return (
    <div className="App">
      {!currentUser ? (
        <>
          <LandingPage 
            onShowSignup={() => setShowSignup(true)}
            onShowLogin={() => setShowLogin(true)}
          />
          {showSignup && (
            <Signup 
              onSuccess={handleSignupSuccess} 
              onClose={() => setShowSignup(false)}
              industries={industries}
              onShowLogin={() => {
                setShowSignup(false);
                setShowLogin(true);
              }}
            />
          )}
          {showLogin && (
            <LoginModal
              onSuccess={handleLoginSuccess}
              onClose={() => setShowLogin(false)}
              onShowSignup={() => {
                setShowLogin(false);
                setShowSignup(true);
              }}
            />
          )}
        </>
      ) : (
        <DashboardNew 
          user={currentUser} 
          onLogout={handleLogout}
          platforms={platforms}
        />
      )}
    </div>
  );
}

export default App;
